import { Alert, Box, Button, Chip, Dialog, DialogActions, DialogContent, DialogTitle, FormControl,
  MenuItem, Paper, Select, Stack, Typography } from '@mui/material';
import { ArrowBack, LinkOutlined, LinkOffOutlined, SaveOutlined } from '@mui/icons-material';
import { useContext, useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { Link } from 'react-router-dom';
import { Franchise } from '../../../app/services/api/generated';
import { LightspeedClientApi, LightspeedConnectionStatus, LightspeedStoreMapping } from '../../../app/services/api/lightspeed';
import PageArea from '../../../components/shared/PageArea';
import PageTitle from '../../../components/shared/PageTitle';
import { UserContext } from '../../../components/shared/useUser';
import useNswagClient from '../../../hooks/api/useNswagClient';

const client = new LightspeedClientApi();

const LightspeedIntegration = () => {
  const { t } = useTranslation('drawer');
  const { selectedStore, hasPermissionTo } = useContext(UserContext);
  const { getFranchises } = useNswagClient();
  const [franchises, setFranchises] = useState<Franchise[]>([]);
  const [franchiseName, setFranchiseName] = useState('');
  const [connection, setConnection] = useState<LightspeedConnectionStatus>();
  const [mapping, setMapping] = useState<Record<string, string>>({});
  const [busy, setBusy] = useState(false);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');
  const [message, setMessage] = useState('');
  const [confirmDisconnect, setConfirmDisconnect] = useState(false);

  const canManage = hasPermissionTo(['LightspeedConnectionManage']);

  useEffect(() => {
    if (!canManage) return;
    void getFranchises().then(result => {
      const available = result.data ?? [];
      setFranchises(available);
      setFranchiseName(available.find(item => item.franchiseName === selectedStore?.franchiseName)?.franchiseName
        ?? (available.length === 1 ? available[0].franchiseName ?? '' : ''));
    }).catch(() => setError(t('lightspeedLoadError')));
  }, [canManage]);

  useEffect(() => {
    if (!franchiseName) { setConnection(undefined); return; }
    let active = true;
    setLoading(true);
    setError('');
    void client.getStatus(franchiseName).then(status => {
      if (!active) return;
      setConnection(status);
      setMapping(Object.fromEntries(status.stores.map(store =>
        [store.storeNumber, store.businessLocationId?.toString() ?? ''])));
    }).catch(() => { if (active) setError(t('lightspeedLoadError')); })
      .finally(() => { if (active) setLoading(false); });
    return () => { active = false; };
  }, [franchiseName]);

  useEffect(() => {
    const result = new URLSearchParams(window.location.search).get('lightspeed');
    if (result === 'cancelled') setMessage(t('lightspeedCancelled'));
    if (result === 'error') setError(t('lightspeedConnectError'));
    if (result === 'connected') setMessage(t('lightspeedAuthorized'));
    if (result) window.history.replaceState({}, '', window.location.pathname);
  }, []);

  const connect = async () => {
    if (!franchiseName) return;
    setBusy(true);
    setError('');
    try {
      const url = await client.connect(franchiseName);
      window.location.assign(url);
    } catch {
      setError(t('lightspeedConnectError'));
      setBusy(false);
    }
  };

  const save = async () => {
    if (!franchiseName || !connection) return;
    const mappings: LightspeedStoreMapping[] = Object.entries(mapping)
      .filter(([, location]) => location !== '')
      .map(([storeNumber, location]) => ({ storeNumber, businessLocationId: Number(location) }));
    if (!mappings.length || new Set(mappings.map(item => item.businessLocationId)).size !== mappings.length) {
      setError(t('lightspeedMappingError'));
      return;
    }
    setBusy(true);
    setError('');
    try {
      const status = await client.saveLocations(franchiseName, mappings);
      setConnection(status);
      setMessage(t('lightspeedSaved'));
    } catch { setError(t('lightspeedSaveError')); }
    finally { setBusy(false); }
  };

  const disconnect = async () => {
    if (!franchiseName) return;
    setBusy(true);
    setError('');
    try {
      await client.disconnect(franchiseName);
      setConnection(await client.getStatus(franchiseName));
      setMapping({});
      setMessage(t('lightspeedDisconnected'));
    } catch { setError(t('lightspeedDisconnectError')); }
    finally { setBusy(false); setConfirmDisconnect(false); }
  };

  if (!canManage) return null;
  return <PageArea>
    <Button component={Link}
      to="/integrations"
      startIcon={<ArrowBack />}
      size="md"
      sx={{ mb: 3 }}>
      {t('integrations')}
    </Button>
    <PageTitle subTitle={t('lightspeedDescription')}>{t('lightspeedIntegration')}</PageTitle>
    <Stack spacing={3}
      sx={{ maxWidth: 800, mt: 3 }}>
      {error && <Alert severity="error">{error}</Alert>}
      {message && <Alert severity="success"
        onClose={() => setMessage('')}>{message}</Alert>}
      <FormControl fullWidth>
        <Typography component="label"
          htmlFor="lightspeed-franchise"
          variant="textSM"
          sx={{ mb: 1, fontWeight: 600 }}>
          {t('lightspeedFranchise')}
        </Typography>
        <Select id="lightspeed-franchise"
          value={franchiseName}
          displayEmpty
          onChange={event => setFranchiseName(event.target.value)}
          disabled={busy}>
          {franchises.map(franchise => <MenuItem key={franchise.franchiseName}
            value={franchise.franchiseName}>
            {franchise.franchiseName}
          </MenuItem>)}
        </Select>
      </FormControl>
      {loading && <Typography>{t('lightspeedLoading')}</Typography>}
      {!loading && connection && <Paper variant="outlined"
        sx={{ p: { xs: 2, sm: 3 }, borderRadius: 2 }}>
        <Stack spacing={2.5}>
          <Stack direction="row"
            alignItems="center"
            spacing={1.5}
            flexWrap="wrap"
            useFlexGap>
            <Typography variant="textLG"
              sx={{ fontWeight: 600 }}>{t('lightspeedStatus')}</Typography>
            <Chip size="small"
              color={connection.status === 'Connected' ? 'success' : 'default'}
              label={t(`lightspeedStatus${connection.status}`)}
              sx={{ ml: 1.5 }} />
          </Stack>
          {connection.lastSuccessfulSyncAt && <Typography>
            {t('lightspeedLastSync')}: {new Date(connection.lastSuccessfulSyncAt).toLocaleString()}
          </Typography>}
          <Stack direction={{ xs: 'column', sm: 'row' }}
            spacing={1.5}
            alignItems={{ sm: 'center' }}>
            <Button variant="primary"
              size="md"
              startIcon={<LinkOutlined />}
              onClick={connect}
              disabled={busy}>
              {connection.status === 'NotConnected' ? t('lightspeedConnect') : t('lightspeedReconnect')}
            </Button>
            {connection.status !== 'NotConnected' && <Button
              variant="badSecondary"
              size="md"
              startIcon={<LinkOffOutlined />}
              onClick={() => setConfirmDisconnect(true)}
              disabled={busy}>
              {t('lightspeedDisconnect')}
            </Button>}
          </Stack>
        </Stack>
      </Paper>}
      {!loading && connection && connection.locations.length > 0 && <Paper variant="outlined"
        sx={{ p: { xs: 2, sm: 3 }, borderRadius: 2 }}>
        <Stack spacing={2.5}>
          <Box>
            <Typography variant="textLG"
              sx={{ fontWeight: 600 }}>{t('lightspeedMapLocations')}</Typography>
            <Typography color="text.secondary"
              sx={{ mt: 1 }}>{t('lightspeedMapDescription')}</Typography>
          </Box>
          {connection.stores.map(store => <FormControl key={store.storeNumber}
            fullWidth>
            <Typography component="label"
              htmlFor={`lightspeed-store-${store.storeNumber}`}
              variant="textSM"
              sx={{ mb: 1, fontWeight: 600 }}>{store.name} ({store.storeNumber})</Typography>
            <Select id={`lightspeed-store-${store.storeNumber}`}
              value={mapping[store.storeNumber] ?? ''}
              displayEmpty
              disabled={busy}
              onChange={event => setMapping(current => ({ ...current, [store.storeNumber]: event.target.value }))}>
              <MenuItem value="">{t('lightspeedUnmapped')}</MenuItem>
              {connection.locations.map(location => <MenuItem key={location.businessLocationId}
                value={location.businessLocationId.toString()}>
                {location.businessName} — {location.name} ({location.businessLocationId})
              </MenuItem>)}
            </Select>
          </FormControl>)}
          <Button variant="primary"
            size="md"
            startIcon={<SaveOutlined />}
            onClick={save}
            disabled={busy}
            sx={{ alignSelf: { sm: 'flex-start' } }}>{t('lightspeedSaveMappings')}</Button>
        </Stack>
      </Paper>}
    </Stack>
    <Dialog open={confirmDisconnect}
      onClose={() => setConfirmDisconnect(false)}>
      <DialogTitle>{t('lightspeedDisconnect')}</DialogTitle>
      <DialogContent>{t('lightspeedDisconnectConfirm')}</DialogContent>
      <DialogActions>
        <Button onClick={() => setConfirmDisconnect(false)}>{t('lightspeedCancel')}</Button>
        <Button color="error"
          onClick={disconnect}
          disabled={busy}>{t('lightspeedDisconnect')}</Button>
      </DialogActions>
    </Dialog>
  </PageArea>;
};

export default LightspeedIntegration;
